// @ts-strict-ignore
import { uniq } from 'lodash-es'
import { UseFormSetValue } from 'react-hook-form'
import {
  Contact,
  ContactEntities,
  ContactEntityCompany,
  ContactEntityIndividual,
  InstrumentTypes,
} from '@node-space/types'
import { UKRAINE } from 'constants/contacts'
import {
  BeneficiaryDetails,
  BeneficiaryDetailsCurrency,
  BeneficiaryEntityType,
  BeneficiaryTransferDestination,
  BeneficiaryType,
  IntermediaryBank,
  PaymentMethod,
  PaymentMethods,
  ValidateIbanResponse,
} from 'types/beneficiaries'
import {
  AccountHolderName,
  BankAccountPaymentInstrument,
  ContactInstrument,
  ContactPaymentInstruments,
  CreateContactBankAccountRequest,
  IdentificationCodeTypes,
} from 'types/contacts'
import { ConfirmPayeeBeneficiaryParams } from 'types/payoutsV2'
import { isIbanFormat } from 'utils/beneficiaries'
import {
  ContactFiatInstrumentFormData,
  ContactFiatInstrumentFormKeys,
} from '../schemas/contactFiatInstrumentSchema'
import { ContactFormData, ContactFormKeys } from '../schemas/contactSchema'
import { AddressType, FieldsFetchedFromIbanValidation, Step, Steps } from '../types/fiatInstruments'
import {
  BANK_ADDRESS_FIELDS,
  BASIC_BANK_ACCOUNT_FIELDS,
  BASIC_FIELDS,
  BENEFICIARY_ADDRESS_FIELDS,
  CURRENCY_DETAILS_FIELDS,
  EEA_COUNTRY_CODES,
  INTERMEDIARY_BANK_FIELDS,
} from './constants.fiatInstruments'
import { isContactEntityCompany, isContactEntityIndividual } from './utils'

const BIC_REGEX = /[a-zA-Z]/

export const isBeneficiaryAddressFieldsRequired = ({
  field,
  formFields,
  enableHybridAddress,
}: {
  formFields: ContactFiatInstrumentFormData
  field: string
  enableHybridAddress?: boolean
}) => {
  const { currency, paymentMethods, accountNumber, sameAsContactAddress } = formFields || {}
  const isSwift = paymentMethods?.includes(PaymentMethods.SWIFT)
  const isAddressLine2Field = field === 'beneficiaryAddressLine2'
  const isCountryField = field === 'beneficiaryCountryCode'

  if (isAddressLine2Field || sameAsContactAddress) {
    return false
  }

  if (enableHybridAddress) {
    return true
  }

  if (isSwift || isCountryField) {
    return true
  }

  switch (currency) {
    case BeneficiaryDetailsCurrency.USD: {
      return (
        paymentMethods?.includes(PaymentMethods.FEDWIRE) ||
        paymentMethods?.includes(PaymentMethods.FEDNOW)
      )
    }
    case BeneficiaryDetailsCurrency.EUR: {
      const isEurAndCountryIsNonEea =
        currency === BeneficiaryDetailsCurrency.EUR &&
        !EEA_COUNTRY_CODES.includes(accountNumber?.slice(0, 2).toUpperCase() ?? '')
      return isEurAndCountryIsNonEea
    }
    case BeneficiaryDetailsCurrency.GBP:
    default:
      return false
  }
}

export const isAddressFieldsRequired = ({
  currency,
  isSwiftPaymentMethodSelected,
}: {
  currency?: string
  isSwiftPaymentMethodSelected?: boolean
}) => {
  const isLocalUSD = !isSwiftPaymentMethodSelected && currency === BeneficiaryDetailsCurrency.USD

  return isSwiftPaymentMethodSelected || isLocalUSD
}

export const getFieldsToValidate = (
  step: Step,
  formValues: ContactFiatInstrumentFormData
): ContactFiatInstrumentFormKeys[] => {
  const { paymentMethods, isIntermediaryBankDetailsRequired } = formValues || {}
  const isInternational = paymentMethods?.includes(PaymentMethods.SWIFT)

  switch (step) {
    case Steps.CURRENCY_DETAILS:
      return CURRENCY_DETAILS_FIELDS
    case Steps.BENEFICIARY_DETAILS:
      return [...BASIC_FIELDS, ...BENEFICIARY_ADDRESS_FIELDS]
    case Steps.BANK_DETAILS:
      return isInternational
        ? [
            ...BASIC_BANK_ACCOUNT_FIELDS,
            ...BANK_ADDRESS_FIELDS,
            ...(isIntermediaryBankDetailsRequired ? INTERMEDIARY_BANK_FIELDS : []),
          ]
        : [...BASIC_BANK_ACCOUNT_FIELDS]
    default:
      return []
  }
}

export const getIsValidIban = ({
  paymentMethods,
  currency,
  ibanBankDetails,
}: {
  currency?: string
  paymentMethods?: PaymentMethod[]
  ibanBankDetails?: ValidateIbanResponse
}) => {
  switch (currency) {
    case 'AED':
      return !!ibanBankDetails?.bankAccount?.bankCode
    case 'EUR': {
      const isSwiftOnly =
        paymentMethods?.includes(PaymentMethods.SWIFT) && paymentMethods?.length === 1
      return isSwiftOnly
        ? !!ibanBankDetails?.bankAccount?.bankCode
        : (ibanBankDetails?.supportedPaymentMethods?.includes('SEPA_CT') ||
            ibanBankDetails?.supportedPaymentMethods?.includes('SEPA_INST')) &&
            !ibanBankDetails?.validationFailed?.length
    }
    case 'GBP':
    case 'USD':
    default:
      return !!ibanBankDetails?.bankAccount?.bankCode
  }
}

export const cleanIbanValidationResponse = (
  bankDetails: ValidateIbanResponse
): ValidateIbanResponse => {
  return {
    bankAccount: {
      type: bankDetails?.bankAccount?.type,
      bankName: bankDetails?.bankAccount?.bankName?.trim(),
      accountNumber: bankDetails?.bankAccount?.accountNumber?.trim(),
      bankCode: bankDetails?.bankAccount?.bankCode?.trim(),
      bankAddress: {
        addressLine1: bankDetails?.bankAccount?.bankAddress?.addressLine1?.trim(),
        addressLine2: bankDetails?.bankAccount?.bankAddress?.addressLine2?.trim(),
        city: bankDetails?.bankAccount?.bankAddress?.city?.trim(),
        region: bankDetails?.bankAccount?.bankAddress?.region?.trim(),
        postCode: bankDetails?.bankAccount?.bankAddress?.postCode?.trim(),
        countryCode: bankDetails?.bankAccount?.bankAddress?.countryCode?.trim(),
      },
    },
    validationFailed: bankDetails?.validationFailed,
    supportedPaymentMethods: bankDetails?.supportedPaymentMethods,
  }
}

const getBaseFieldsFetchedFromIbanValidation = (
  cleanDetail: ValidateIbanResponse
): FieldsFetchedFromIbanValidation => {
  const { bankName, bankAddress, accountNumber } = cleanDetail?.bankAccount || {}

  return {
    ...(accountNumber && {
      accountNumber,
    }),
    ...(bankName && {
      bankName,
    }),
    ...(bankAddress?.addressLine1 && {
      bankAddressLine1: bankAddress.addressLine1,
    }),
    ...(bankAddress?.addressLine2 && {
      bankAddressLine2: bankAddress.addressLine2,
    }),
    ...(bankAddress?.city && {
      bankCity: bankAddress.city,
    }),
    ...(bankAddress?.postCode && {
      bankPostCode: bankAddress.postCode,
    }),
    ...(bankAddress?.region && {
      bankRegion: bankAddress.region,
    }),
    ...(bankAddress?.countryCode && {
      bankCountryCode: bankAddress.countryCode,
    }),
  }
}

export const setBankAccountFieldsFetchedFromIbanValidation = (
  formValues: ContactFiatInstrumentFormData,
  ibanValidationResponse: ValidateIbanResponse
): FieldsFetchedFromIbanValidation => {
  const cleanDetail = cleanIbanValidationResponse(ibanValidationResponse)

  const { paymentMethods, currency } = formValues || {}
  const isSWIFT = paymentMethods?.includes(PaymentMethods.SWIFT)
  const isEur = currency === BeneficiaryDetailsCurrency.EUR
  const isGbp = currency === BeneficiaryDetailsCurrency.GBP

  // EUR
  const isEurSepaOnly =
    isEur &&
    (paymentMethods?.includes(PaymentMethods.SEPA) ||
      paymentMethods?.includes(PaymentMethods.SEPA_INSTANT)) &&
    !isSWIFT
  const isEurSepaAndSwift =
    isEur &&
    (paymentMethods?.includes(PaymentMethods.SEPA) ||
      paymentMethods?.includes(PaymentMethods.SEPA_INSTANT)) &&
    paymentMethods?.includes(PaymentMethods.SWIFT)

  // GBP
  const isGbpSepaOnly = isGbp && paymentMethods?.includes(PaymentMethods.FASTER_PAYMENT) && !isSWIFT
  const isGbpSepaAndSwift =
    isGbp &&
    paymentMethods?.includes(PaymentMethods.FASTER_PAYMENT) &&
    paymentMethods?.includes(PaymentMethods.SWIFT)

  const { bankCode } = cleanDetail?.bankAccount || {}
  const baseFields = getBaseFieldsFetchedFromIbanValidation(cleanDetail)

  const shouldSetBankCode = isEurSepaOnly || isEurSepaAndSwift || isGbpSepaOnly || isGbpSepaAndSwift
  return {
    ...baseFields,
    ...(isSWIFT && bankCode && { swiftCode: bankCode }),
    ...(bankCode &&
      shouldSetBankCode && {
        bankCode,
      }),
  }
}

export const setFieldsFetchedFromIbanValidation = (
  ibanValidationResponse: ValidateIbanResponse
): FieldsFetchedFromIbanValidation => {
  const cleanDetail = cleanIbanValidationResponse(ibanValidationResponse)
  const { bankCode } = cleanDetail?.bankAccount || {}
  const baseFields = getBaseFieldsFetchedFromIbanValidation(cleanDetail)

  return {
    ...baseFields,
    ...(bankCode && { bankCode }),
  }
}

export const resetBankAddress = (
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>,
  prefix: AddressType
) => {
  setValue(`${prefix}Name` as keyof ContactFiatInstrumentFormData, '')
  setValue(`${prefix}AddressLine1` as keyof ContactFiatInstrumentFormData, '')
  setValue(`${prefix}AddressLine2` as keyof ContactFiatInstrumentFormData, '')
  setValue(`${prefix}City` as keyof ContactFiatInstrumentFormData, '')
  setValue(`${prefix}Region` as keyof ContactFiatInstrumentFormData, '')
  setValue(`${prefix}PostCode` as keyof ContactFiatInstrumentFormData, '')
}

const mapBeneficiaryEntityType = (beneficiaryEntityType?: string) => {
  if (beneficiaryEntityType === BeneficiaryEntityType.BUSINESS) {
    return ContactEntities.COMPANY
  }
  if (beneficiaryEntityType === BeneficiaryEntityType.INDIVIDUAL) {
    return ContactEntities.INDIVIDUAL
  }
  return ContactEntities.COMPANY
}

const mapIndividualBeneficiaryToForm = (
  beneficiary: BeneficiaryDetails
): Partial<ContactFormData> => {
  // Convert dateOfBirth for SingleDate component
  const formattedDateOfBirth = beneficiary?.dateOfBirth
    ? beneficiary.dateOfBirth.replace(/\//g, ',')
    : ''

  return {
    entityType: ContactEntities?.INDIVIDUAL,
    firstName: beneficiary?.firstName || '',
    lastName: beneficiary?.lastName || '',
    contactDescription: beneficiary?.alias || '',
    addressLine1: beneficiary?.address?.addressLine1 || '',
    addressLine2: beneficiary?.address?.addressLine2 || '',
    city: beneficiary?.address?.city || '',
    postCode: beneficiary?.address?.postCode || '',
    region: beneficiary?.address?.region || '',
    countryOfResidence: beneficiary?.addressCountry || beneficiary?.address?.country || '',
    dateOfBirth: formattedDateOfBirth,
    isSelfOwned: beneficiary?.beneficiaryType === BeneficiaryType.FIRST_PARTY,
  }
}

const mapCompanyBeneficiaryToForm = (beneficiary: BeneficiaryDetails): Partial<ContactFormData> => {
  return {
    entityType: ContactEntities.COMPANY,
    companyName: beneficiary?.businessName || '',
    contactDescription: beneficiary?.alias || '',
    companyRegistrationCountry: beneficiary?.addressCountry || beneficiary?.address?.country || '',
    addressLine1: beneficiary?.address?.addressLine1 || '',
    addressLine2: beneficiary?.address?.addressLine2 || '',
    city: beneficiary?.address?.city || '',
    postCode: beneficiary?.address?.postCode || '',
    region: beneficiary?.address?.region || '',
    isSelfOwned: beneficiary?.beneficiaryType === BeneficiaryType.FIRST_PARTY,
  }
}

const mapBeneficiaryToContactForm = (beneficiary: BeneficiaryDetails): Partial<ContactFormData> => {
  const entityType = mapBeneficiaryEntityType(beneficiary.entityType)

  if (entityType === ContactEntities.COMPANY) {
    return mapCompanyBeneficiaryToForm(beneficiary)
  }

  return mapIndividualBeneficiaryToForm(beneficiary)
}

export const populateContactFormWithBeneficiaryDetails = (
  beneficiary: BeneficiaryDetails,
  setValue: UseFormSetValue<ContactFormData>
) => {
  const formData = mapBeneficiaryToContactForm(beneficiary)

  Object.entries(formData).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      setValue(key as keyof ContactFormData, value, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
  })
}

const mapBeneficiaryPaymentMethods = (beneficiary: BeneficiaryDetails): string[] => {
  switch (beneficiary?.currency) {
    case BeneficiaryDetailsCurrency.USD: {
      if (beneficiary?.paymentMethod) {
        return [beneficiary?.paymentMethod]
      }
      return beneficiary?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
        ? [PaymentMethods.SWIFT]
        : []
    }
    case BeneficiaryDetailsCurrency.EUR:
      return beneficiary?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
        ? [PaymentMethods.SWIFT]
        : [PaymentMethods.SEPA, PaymentMethods.SEPA_INSTANT]
    case BeneficiaryDetailsCurrency.GBP:
      return beneficiary?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
        ? [PaymentMethods.SWIFT]
        : [PaymentMethods.FASTER_PAYMENT]
    case BeneficiaryDetailsCurrency.AED:
    case BeneficiaryDetailsCurrency.MXN:
    case BeneficiaryDetailsCurrency.ARS:
    case BeneficiaryDetailsCurrency.BRL:
      return [PaymentMethods.SWIFT]
    case BeneficiaryDetailsCurrency.ZAR:
      return []
    default:
      return []
  }
}

const mapBeneficiaryBankCodesToBankAccountBankCodes = (beneficiary: BeneficiaryDetails) => {
  const { fiat, transferDestination } = beneficiary || {}
  const { code, nid } = fiat || {}
  const isSwift = transferDestination === BeneficiaryTransferDestination.INTERNATIONAL

  return isSwift
    ? {
        bankCode: nid || '',
        swiftCode: code,
      }
    : {
        bankCode: code,
        swiftCode: '',
      }
}

const mapBeneficiaryIntermediaryBankCodesToBankAccountBankCodes = (
  intermediaryBank: IntermediaryBank
) => {
  const { code, nid } = intermediaryBank || {}

  return {
    intermediaryBankCode: nid || '',
    intermediaryBankSwiftCode: code,
  }
}

const mapBeneficiaryToFiatInstrumentForm = (
  beneficiary: BeneficiaryDetails
): Partial<ContactFiatInstrumentFormData> => {
  const accountDetails = beneficiary?.fiat
  const bankAddress = accountDetails?.bankAddress
  const intermediaryBank = accountDetails?.intermediaryBank

  const isLocal = beneficiary?.transferDestination === BeneficiaryTransferDestination.LOCAL
  const isInternational =
    beneficiary?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
  const isUSD = beneficiary?.currency === BeneficiaryDetailsCurrency.USD
  const isZAR = beneficiary?.currency === BeneficiaryDetailsCurrency.ZAR

  const paymentMethods = mapBeneficiaryPaymentMethods(beneficiary)
  const bankCodes = mapBeneficiaryBankCodesToBankAccountBankCodes(beneficiary)

  const contactBankAccount: Partial<ContactFiatInstrumentFormData> = {
    // Basic beneficiary details
    transferDestination: beneficiary?.transferDestination || '',
    paymentMethods,
    entityType: beneficiary?.entityType || '',
    description: beneficiary?.alias || '',
    currency: beneficiary?.currency || '',
    // Account holder details
    firstName: beneficiary?.firstName || '',
    lastName: beneficiary?.lastName || '',
    businessName: beneficiary?.businessName || '',
    // Bank account details
    accountNumber: accountDetails?.accountNumber || '',
    accountType: accountDetails?.accountType || '',
    bankName: accountDetails?.bankName || '',
    // bankCode: accountDetails?.code || '',
    ...bankCodes,
    beneficiaryAddressLine1: beneficiary?.address?.addressLine1 || '',
    beneficiaryAddressLine2: beneficiary?.address?.addressLine2 || '',
    beneficiaryCity: beneficiary?.address?.city || '',
    beneficiaryRegion: beneficiary?.address?.region || '',
    beneficiaryPostCode: beneficiary?.address?.postCode || '',
    beneficiaryCountryCode: beneficiary?.addressCountry || '',
  }

  // Handle currency-specific fields
  if (isLocal) {
    // USD local needs account confirmation
    if (isUSD) {
      contactBankAccount.accountNumberConfirm = accountDetails?.accountNumber || ''
    }

    // ZAR uses bankNameCode for bank selection
    if (isZAR) {
      contactBankAccount.bankNameCode = accountDetails?.code || ''
    }
  } else if (isInternational) {
    contactBankAccount.swiftCode = accountDetails?.code || ''
  }

  if (bankAddress) {
    contactBankAccount.bankAddressLine1 = bankAddress.addressLine1 || ''
    contactBankAccount.bankAddressLine2 = bankAddress.addressLine2 ?? ''
    contactBankAccount.bankCity = bankAddress.city || ''
    contactBankAccount.bankRegion = bankAddress.region || ''
    contactBankAccount.bankPostCode = bankAddress.postCode || ''
    contactBankAccount.bankCountryCode = bankAddress.country || ''
    contactBankAccount.bankCountryName = bankAddress.country || ''
  }

  // Add intermediary bank details if present
  if (intermediaryBank && isInternational) {
    const intermediaryBankCodes =
      mapBeneficiaryIntermediaryBankCodesToBankAccountBankCodes(intermediaryBank)

    return {
      ...contactBankAccount,
      isIntermediaryBankDetailsRequired: true,
      ...intermediaryBankCodes,
      ...(intermediaryBank.bankName && {
        intermediaryBankName: intermediaryBank.bankName,
      }),
      ...(intermediaryBank.bankAddress?.addressLine1 && {
        intermediaryBankAddressLine1: intermediaryBank.bankAddress.addressLine1,
      }),
      ...(intermediaryBank.bankAddress?.addressLine2 && {
        intermediaryBankAddressLine2: intermediaryBank.bankAddress.addressLine2,
      }),
      ...(intermediaryBank.bankAddress?.city && {
        intermediaryBankCity: intermediaryBank.bankAddress.city,
      }),
      ...(intermediaryBank.bankAddress?.region && {
        intermediaryBankRegion: intermediaryBank.bankAddress.region,
      }),
      ...(intermediaryBank.bankAddress?.postCode && {
        intermediaryBankPostCode: intermediaryBank.bankAddress.postCode,
      }),
      ...(intermediaryBank.bankAddress?.country && {
        intermediaryBankCountryCode: intermediaryBank.bankAddress.country,
      }),
    }
  }

  return contactBankAccount
}

export const populateInstrumentFormWithBeneficiaryDetails = (
  beneficiary: BeneficiaryDetails,
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
) => {
  const formData = mapBeneficiaryToFiatInstrumentForm(beneficiary)

  Object.entries(formData).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      setValue(key as keyof ContactFiatInstrumentFormData, value, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
  })
}

export const mapFormDataToBeneficiaryDetails = (
  contact: Contact,
  formData: ContactFiatInstrumentFormData
): BeneficiaryDetails => {
  const { entity } = contact || ({} as Contact)
  const isCompany = isContactEntityCompany(entity)
  const isIndividual = isContactEntityIndividual(entity)

  return {
    ...formData,
    currency: formData?.currency as BeneficiaryDetailsCurrency,
    entityType: formData?.entityType as BeneficiaryEntityType,
    transferDestination: BeneficiaryTransferDestination.LOCAL,
    addressCountry: formData?.beneficiaryCountryCode || '',
    fiat: {
      accountNumber: formData?.accountNumber || '',
      code: formData?.bankCode || '',
      bankName: formData?.bankName || '',
    },
    ...(isCompany && {
      businessName: (contact?.entity as ContactEntityCompany)?.legalName || '',
    }),
    ...(isIndividual && {
      firstName: (contact?.entity as ContactEntityIndividual)?.firstName || '',
      lastName: (contact?.entity as ContactEntityIndividual)?.lastName || '',
    }),
  }
}

export const getConfirmPayeeBeneficiaryParams = (
  beneficiary: BeneficiaryDetails
): ConfirmPayeeBeneficiaryParams => {
  return {
    ...(beneficiary?.entityType === BeneficiaryEntityType.INDIVIDUAL && {
      firstName: beneficiary?.firstName,
      lastName: beneficiary?.lastName,
    }),
    ...(beneficiary?.entityType === BeneficiaryEntityType.BUSINESS && {
      businessName: beneficiary?.businessName,
    }),
    accountNumber: beneficiary?.fiat?.accountNumber,
    bankCode: beneficiary?.fiat?.code,
    currency: beneficiary?.currency,
    transferDestination: beneficiary?.transferDestination,
  }
}

export const getPaymentMethods = (formValues: ContactFiatInstrumentFormData) => {
  const isEurBankAccount = formValues?.currency === BeneficiaryDetailsCurrency.EUR

  if (isEurBankAccount) {
    const paymentMethods = formValues?.paymentMethods ?? []
    const isLocal = paymentMethods.includes(PaymentMethods.SEPA)

    return uniq([...paymentMethods, ...(isLocal ? [PaymentMethods.SEPA_INSTANT] : [])])
  }

  return uniq(formValues?.paymentMethods ?? [])
}

const withIdentificationCodeFields = (identificationCode: string, currency: string) => {
  if (!identificationCode || !currency) return

  const isBic = BIC_REGEX.test(identificationCode)

  if (isBic) {
    return IdentificationCodeTypes.BIC
  }

  switch (currency) {
    case BeneficiaryDetailsCurrency.GBP:
    case BeneficiaryDetailsCurrency.USD:
      return IdentificationCodeTypes.NID
    case BeneficiaryDetailsCurrency.EUR:
    case BeneficiaryDetailsCurrency.ARS:
    case BeneficiaryDetailsCurrency.BRL:
    case BeneficiaryDetailsCurrency.MXN:
      return IdentificationCodeTypes.BIC
    default:
      return
  }
}

const getEurBankCode = (formValues: ContactFiatInstrumentFormData) => {
  const { bankCode, swiftCode, paymentMethods } = formValues ?? {}
  const resolvedBankCode = bankCode || swiftCode
  const isLocalOnly = !!paymentMethods?.length && !paymentMethods?.includes(PaymentMethods.SWIFT)

  if (isLocalOnly) {
    return { identificationCode: bankCode }
  }

  return { swiftCode, identificationCode: resolvedBankCode }
}

const getGbpBankCode = (formValues: ContactFiatInstrumentFormData) => {
  const { bankCode, swiftCode, paymentMethods } = formValues ?? {}
  const resolvedBankCode = bankCode || swiftCode
  const isSwift = paymentMethods?.includes(PaymentMethods.SWIFT)
  const isSwiftOnly = isSwift && paymentMethods?.length === 1
  const isLocal = paymentMethods?.includes(PaymentMethods.FASTER_PAYMENT)
  const isLocalOnly =
    !!paymentMethods?.length && paymentMethods?.includes(PaymentMethods.FASTER_PAYMENT)
  const isIban = isIbanFormat(formValues?.accountNumber)

  if (isSwiftOnly) {
    return { swiftCode, identificationCode: resolvedBankCode }
  }

  if (isSwift && isLocal) {
    return {
      swiftCode,
      ...(isIban && { identificationCode: swiftCode }),
      ...(!isIban && { identificationCode: bankCode }),
    }
  }

  if (isLocalOnly) {
    return { identificationCode: bankCode }
  }
  return {}
}

const getBankCodes = (formValues: ContactFiatInstrumentFormData) => {
  const { currency, bankCode, swiftCode, paymentMethods } = formValues ?? {}
  const resolvedBankCode = bankCode || swiftCode
  const isSwift = paymentMethods?.includes(PaymentMethods.SWIFT)

  switch (currency) {
    case BeneficiaryDetailsCurrency.USD:
      return isSwift
        ? {
            swiftCode: swiftCode,
            identificationCode: resolvedBankCode,
          }
        : {
            identificationCode: bankCode,
          }
    case BeneficiaryDetailsCurrency.EUR:
      return { ...getEurBankCode(formValues) }
    case BeneficiaryDetailsCurrency.GBP:
      return { ...getGbpBankCode(formValues) }
    default:
      return isSwift
        ? {
            swiftCode,
            ...(bankCode && { identificationCode: bankCode }),
          }
        : { identificationCode: bankCode }
  }
}

export const getRequestBankPayload = (formValues: ContactFiatInstrumentFormData) => {
  const {
    bankName,
    bankAddressLine1,
    bankAddressLine2,
    bankCity,
    bankRegion,
    bankPostCode,
    bankCountryCode,
  } = formValues ?? {}

  const hasAddressFields =
    !!bankAddressLine1 ||
    !!bankAddressLine2 ||
    !!bankRegion ||
    !!bankCity ||
    !!bankPostCode ||
    !!bankCountryCode

  const { identificationCode, swiftCode } = getBankCodes(formValues)
  const identificationCodeType = withIdentificationCodeFields(
    identificationCode,
    formValues?.currency
  )

  return {
    ...(bankName && { name: bankName }),
    ...(identificationCode && { identificationCode }),
    ...(swiftCode && { swiftCode }),
    ...(identificationCodeType && {
      identificationCodeType,
      identificationCodeValue: identificationCode,
    }),
    ...(hasAddressFields && {
      address: {
        ...(bankAddressLine1 && { addressLine1: bankAddressLine1 }),
        ...(bankAddressLine2 && { addressLine2: bankAddressLine2 }),
        ...(bankRegion && { region: bankRegion }),
        ...(bankCity && { city: bankCity }),
        ...(bankPostCode && { postCode: bankPostCode }),
        ...(bankCountryCode && { country: bankCountryCode }),
      },
    }),
  }
}

export const getRequestIntermediaryBankPayload = (formValues: ContactFiatInstrumentFormData) => {
  const {
    currency,
    paymentMethods,
    intermediaryBankCode,
    intermediaryBankName,
    intermediaryBankAddressLine1,
    intermediaryBankAddressLine2,
    intermediaryBankCity,
    intermediaryBankRegion,
    intermediaryBankPostCode,
    intermediaryBankCountryCode,
    intermediaryBankSwiftCode,
    isIntermediaryBankDetailsRequired,
  } = formValues ?? {}
  const isSWIFT = paymentMethods?.includes(PaymentMethods.SWIFT)
  const hasIntermediaryBank = isSWIFT && isIntermediaryBankDetailsRequired

  if (!hasIntermediaryBank) return

  const resolvedBankCode = intermediaryBankCode || intermediaryBankSwiftCode
  const identificationCodeType = withIdentificationCodeFields(resolvedBankCode, currency)

  return [
    {
      name: intermediaryBankName,
      swiftCode: intermediaryBankSwiftCode,
      identificationCode: resolvedBankCode,
      ...(identificationCodeType && {
        identificationCodeType,
        identificationCodeValue: resolvedBankCode,
      }),
      address: {
        ...(intermediaryBankAddressLine1 && { addressLine1: intermediaryBankAddressLine1 }),
        ...(intermediaryBankAddressLine2 && { addressLine2: intermediaryBankAddressLine2 }),
        ...(intermediaryBankCity && { city: intermediaryBankCity }),
        ...(intermediaryBankRegion && { region: intermediaryBankRegion }),
        ...(intermediaryBankPostCode && { postCode: intermediaryBankPostCode }),
        ...(intermediaryBankCountryCode && { country: intermediaryBankCountryCode }),
      },
    },
  ]
}

const getAccountHolderName = (
  contact: Contact,
  formValues: ContactFiatInstrumentFormData
): AccountHolderName | undefined => {
  const isBusinessContact = contact?.entity?.type === ContactEntities.COMPANY

  if (formValues?.sameAccountHolderNameAsContact) {
    return
  }

  return isBusinessContact
    ? { legalName: formValues?.businessName }
    : {
        firstName: formValues?.firstName,
        lastName: formValues?.lastName,
      }
}

export const getRequestPayload = (
  contact: Contact,
  formValues: ContactFiatInstrumentFormData
): CreateContactBankAccountRequest => {
  const {
    currency,
    description,
    accountNumber,
    beneficiaryAddressLine1,
    beneficiaryAddressLine2,
    beneficiaryRegion,
    beneficiaryCity,
    beneficiaryCountryCode,
    beneficiaryPostCode,
    accountType,
    paymentReference,
  } = formValues ?? {}

  const bankDetails = getRequestBankPayload(formValues)
  const intermediaryBankDetails = getRequestIntermediaryBankPayload(formValues)
  const accountHolderName = getAccountHolderName(contact, formValues)
  const hasBeneficiaryAddressFields =
    !!beneficiaryAddressLine1 ||
    !!beneficiaryAddressLine2 ||
    !!beneficiaryRegion ||
    !!beneficiaryCity ||
    !!beneficiaryCountryCode ||
    !!beneficiaryPostCode

  const isUSD = currency === BeneficiaryDetailsCurrency.USD

  const request: CreateContactBankAccountRequest = {
    type: InstrumentTypes.BANK_ACCOUNT,
    currency,
    description,
    ...(paymentReference && { defaultRemittanceInformation: paymentReference }),
    accountNumber,
    ...(accountHolderName && { accountHolderName }),
    ...(accountType && isUSD && { accountType }),
    ...(hasBeneficiaryAddressFields &&
      !formValues?.sameAsContactAddress && {
        accountHolderAddress: {
          ...(beneficiaryAddressLine1 && { addressLine1: beneficiaryAddressLine1 }),
          ...(beneficiaryAddressLine2 && { addressLine2: beneficiaryAddressLine2 }),
          ...(beneficiaryRegion && { region: beneficiaryRegion }),
          ...(beneficiaryCity && { city: beneficiaryCity }),
          ...(beneficiaryCountryCode && { country: beneficiaryCountryCode }),
          ...(beneficiaryPostCode && { postCode: beneficiaryPostCode }),
        },
      }),
    bank: {
      ...bankDetails,
    },
    paymentMethods: getPaymentMethods(formValues),
    ...(intermediaryBankDetails && { intermediaryBanks: intermediaryBankDetails }),
  }

  return request
}

/**
 * Normalizes payment methods from API response to form values
 * The API returns ['SEPA_CT', 'SEPA_INST'] but the form only uses 'SEPA_CT' to represent both
 */
const normalizePaymentMethods = (
  paymentMethods: string[] | undefined,
  currency: string | undefined
): string[] => {
  if (!paymentMethods?.length) return []

  // For EUR, if SEPA_CT or SEPA_INST are present, normalize to just SEPA_CT
  if (currency === BeneficiaryDetailsCurrency.EUR) {
    const hasSepa =
      paymentMethods.includes(PaymentMethods.SEPA) ||
      paymentMethods.includes(PaymentMethods.SEPA_INSTANT)
    const hasSwift = paymentMethods.includes(PaymentMethods.SWIFT)

    return [...(hasSepa ? [PaymentMethods.SEPA] : []), ...(hasSwift ? [PaymentMethods.SWIFT] : [])]
  }

  return paymentMethods
}

/**
 * Maps a ContactInstrument (BANK_ACCOUNT type) to ContactFiatInstrumentFormData
 * Used for populating the form when editing an existing bank account instrument
 */
export const mapContactInstrumentToFiatInstrumentForm = (
  contactInstrument: ContactInstrument
): Partial<ContactFiatInstrumentFormData> => {
  const {
    currency,
    description,
    defaultRemittanceInformation,
    accountNumber,
    accountType,
    paymentMethods,
    transferDestination,
    bank,
    accountHolderName,
    accountHolderAddress,
    intermediaryBanks,
  } = contactInstrument || {}

  const normalizedPaymentMethods = normalizePaymentMethods(paymentMethods, currency)
  const isSwift = normalizedPaymentMethods?.includes(PaymentMethods.SWIFT)

  const entityType = accountHolderName?.legalName
    ? BeneficiaryEntityType.COMPANY
    : BeneficiaryEntityType.INDIVIDUAL
  const isCompany = entityType === BeneficiaryEntityType.COMPANY
  const intermediaryBank = intermediaryBanks?.[0]

  const formData: Partial<ContactFiatInstrumentFormData> = {
    currency: currency || '',
    paymentMethods: normalizedPaymentMethods,
    transferDestination: transferDestination || '',
    entityType,
    description: description || '',
    ...(defaultRemittanceInformation && { paymentReference: defaultRemittanceInformation }),
    ...(isCompany
      ? { businessName: accountHolderName?.legalName || '' }
      : { firstName: accountHolderName?.firstName, lastName: accountHolderName?.lastName }),
    ...(accountHolderAddress?.addressLine1 && {
      beneficiaryAddressLine1: accountHolderAddress.addressLine1,
    }),
    ...(accountHolderAddress?.addressLine2 && {
      beneficiaryAddressLine2: accountHolderAddress.addressLine2,
    }),
    ...(accountHolderAddress?.city && { beneficiaryCity: accountHolderAddress.city }),
    ...(accountHolderAddress?.region && { beneficiaryRegion: accountHolderAddress.region }),
    ...(accountHolderAddress?.postCode && { beneficiaryPostCode: accountHolderAddress.postCode }),
    ...(accountHolderAddress?.country && { beneficiaryCountryCode: accountHolderAddress.country }),
    sameAsContactAddress: !accountHolderAddress?.country,
    sameAccountHolderNameAsContact:
      !accountHolderName?.legalName &&
      !accountHolderName?.firstName &&
      !accountHolderName?.lastName,
    // Bank account details
    accountNumber: accountNumber || '',
    ...(accountType && { accountType }),
    ...(bank?.identificationCode && { bankCode: bank.identificationCode }),
    ...(bank?.swiftCode && { swiftCode: bank.swiftCode }),
    ...(bank?.name && { bankName: bank.name }),
    ...(bank?.address?.addressLine1 && { bankAddressLine1: bank.address.addressLine1 }),
    ...(bank?.address?.addressLine2 && { bankAddressLine2: bank.address.addressLine2 }),
    ...(bank?.address?.city && { bankCity: bank.address.city }),
    ...(bank?.address?.region && { bankRegion: bank.address.region }),
    ...(bank?.address?.postCode && { bankPostCode: bank.address.postCode }),
    ...(bank?.address?.country && { bankCountryCode: bank.address.country }),
  }

  // Add intermediary bank details if present
  if (intermediaryBank && isSwift) {
    const formDataWithIntermediaryBank = {
      ...formData,
      isIntermediaryBankDetailsRequired: true,
      ...(intermediaryBank.swiftCode && { intermediaryBankSwiftCode: intermediaryBank.swiftCode }),
      ...(intermediaryBank.identificationCode && {
        intermediaryBankCode: intermediaryBank.identificationCode,
      }),
      ...(intermediaryBank.name && { intermediaryBankName: intermediaryBank.name }),
      ...(intermediaryBank.address?.addressLine1 && {
        intermediaryBankAddressLine1: intermediaryBank.address.addressLine1,
      }),
      ...(intermediaryBank.address?.addressLine2 && {
        intermediaryBankAddressLine2: intermediaryBank.address.addressLine2,
      }),
      ...(intermediaryBank.address?.city && {
        intermediaryBankCity: intermediaryBank.address.city,
      }),
      ...(intermediaryBank.address?.region && {
        intermediaryBankRegion: intermediaryBank.address.region,
      }),
      ...(intermediaryBank.address?.postCode && {
        intermediaryBankPostCode: intermediaryBank.address.postCode,
      }),
      ...(intermediaryBank.address?.country && {
        intermediaryBankCountryCode: intermediaryBank.address.country,
      }),
    }
    return formDataWithIntermediaryBank
  }

  return formData
}

export const getRequiredContactFieldsForCryptoAddress = (contact: Contact) => {
  const isUkraine = contact?.entity?.address?.country === UKRAINE
  const isCompany = contact?.entity?.type === ContactEntities.COMPANY
  const companyEntityFields = contact?.entity as ContactEntityCompany
  const individualEntityFields = contact?.entity as ContactEntityIndividual

  return [
    ...(isCompany && !companyEntityFields?.registrationNumber ? ['companyRegistrationNumber'] : []),
    ...(!isCompany && !individualEntityFields?.dateOfBirth ? ['dateOfBirth'] : []),
    ...(contact?.entity?.address?.addressLine1 ? [] : ['addressLine1']),
    ...(contact?.entity?.address?.postCode ? [] : ['postCode']),
    ...(contact?.entity?.address?.city ? [] : ['city']),
    ...(!contact?.entity?.address?.region && isUkraine ? ['regionCode'] : []),
    ...(contact?.entity?.address?.country
      ? []
      : [isCompany ? 'companyRegistrationCountry' : 'countryOfResidence']),
  ] as ContactFormKeys[]
}

// Extra = registrationNumber, dateOfBirth, addressLine1, postCode, city, region, country, regionCode/region, companyRegistrationCountry/countryOfResidence
export const getRequiredContactFieldsForBankAccount = (
  contact: Contact,
  paymentMethods: PaymentMethod[],
  enableHybridAddress = false
) => {
  const isExtraContactFieldsRequired =
    paymentMethods.includes(PaymentMethods.SWIFT) ||
    paymentMethods.includes(PaymentMethods.FEDWIRE) ||
    paymentMethods.includes(PaymentMethods.FEDNOW) ||
    paymentMethods.includes(PaymentMethods.RTP)

  if (isExtraContactFieldsRequired || enableHybridAddress) {
    const isCompany = contact?.entity?.type === ContactEntities.COMPANY
    const isUkraine = contact?.entity?.address?.country === UKRAINE
    const { addressLine1, postCode, city, region, country } = contact?.entity?.address || {}

    return [
      ...(isCompany && !country ? ['companyRegistrationCountry'] : []),
      ...(!isCompany && !country ? ['countryOfResidence'] : []),
      ...(addressLine1 ? [] : ['addressLine1']),
      ...(postCode ? [] : ['postCode']),
      ...(city ? [] : ['city']),
      ...(region ? [] : [isUkraine ? 'regionCode' : 'region']),
    ] as ContactFormKeys[]
  }

  return []
}

export const mapBankAccountPaymentInstrumentToBeneficiaryDetails = (
  contactPaymentInstrument: ContactPaymentInstruments
): BeneficiaryDetails => {
  const { paymentInstrument, contact } = contactPaymentInstrument || {}
  const bankAccount = paymentInstrument as BankAccountPaymentInstrument
  const { entity } = contact || {}
  const companyEntity = entity as ContactEntityCompany
  const individualEntity = entity as ContactEntityIndividual
  const isCompany = entity?.type === BeneficiaryEntityType.COMPANY

  const {
    accountNumber,
    accountType,
    accountHolderName,
    accountHolderAddress,
    bank,
    intermediaryBanks,
    currency,
    description,
    defaultRemittanceInformation,
  } = bankAccount
  const { firstName, lastName, legalName } = accountHolderName || {}
  const {
    address: bankAddress,
    identificationCode: bankCode,
    name: bankName,
    swiftCode: bankSwiftCode,
  } = bank
  const {
    addressLine1: bankAddressLine1,
    addressLine2: bankAddressLine2,
    city: bankCity,
    postCode: bankPostCode,
    region: bankRegion,
    country: bankCountry,
  } = bankAddress || {}
  const {
    addressLine1: accountHolderAddressLine1,
    addressLine2: accountHolderAddressLine2,
    city: accountHolderAddressCity,
    postCode: accountHolderAddressPostCode,
    region: accountHolderAddressRegion,
    country: accountHolderAddressCountry,
  } = accountHolderAddress || {}

  const entityType = isCompany ? BeneficiaryEntityType.BUSINESS : BeneficiaryEntityType.INDIVIDUAL

  const firstIntermediaryBank = intermediaryBanks?.[0]
  const {
    address: intermediaryBankAddress,
    identificationCode: intermediaryBankCode,
    swiftCode: intermediaryBankSwiftCode,
    name: intermediaryBankName,
  } = firstIntermediaryBank || {}
  const {
    addressLine1: intermediaryBankAddressLine1,
    addressLine2: intermediaryBankAddressLine2,
    city: intermediaryBankCity,
    postCode: intermediaryBankPostCode,
    region: intermediaryBankRegion,
    country: intermediaryBankCountry,
  } = intermediaryBankAddress || {}

  const hasIntermediaryBankAddress =
    !!intermediaryBankAddressLine1 ||
    !!intermediaryBankAddressLine2 ||
    !!intermediaryBankCity ||
    !!intermediaryBankPostCode ||
    intermediaryBankRegion ||
    !!intermediaryBankCountry

  const intermediaryBank = firstIntermediaryBank
    ? {
        code: intermediaryBankSwiftCode || intermediaryBankCode || '',
        ...(intermediaryBankName && { bankName: intermediaryBankName }),
        ...(hasIntermediaryBankAddress && {
          bankAddress: {
            ...(intermediaryBankAddressLine1 && { addressLine1: intermediaryBankAddressLine1 }),
            ...(intermediaryBankAddressLine2 && { addressLine2: intermediaryBankAddressLine2 }),
            ...(intermediaryBankCity && { city: intermediaryBankCity }),
            ...(intermediaryBankPostCode && { postCode: intermediaryBankPostCode }),
            ...(intermediaryBankRegion && { region: intermediaryBankRegion }),
            ...(intermediaryBankCountry && { country: intermediaryBankCountry }),
          },
        }),
      }
    : undefined

  const hasLocalPaymentMethod =
    !bankAccount?.paymentMethods?.length ||
    bankAccount?.paymentMethods?.filter(paymentMethod => paymentMethod !== PaymentMethods.SWIFT)
      .length > 0

  const transferDestination = hasLocalPaymentMethod
    ? BeneficiaryTransferDestination.LOCAL
    : BeneficiaryTransferDestination.INTERNATIONAL

  return {
    alias: description || '',
    currency: currency as BeneficiaryDetailsCurrency,
    entityType,
    addressCountry: accountHolderAddressCountry || '',
    transferDestination,
    beneficiaryPaymentReference: defaultRemittanceInformation || '',
    ...(isCompany && {
      businessName: legalName || companyEntity?.legalName || '',
    }),
    ...(!isCompany && {
      firstName: firstName || individualEntity?.firstName || '',
      lastName: lastName || individualEntity?.lastName || '',
    }),
    address: {
      addressLine1: accountHolderAddressLine1 || '',
      addressLine2: accountHolderAddressLine2 || '',
      city: accountHolderAddressCity || '',
      postCode: accountHolderAddressPostCode || '',
      region: accountHolderAddressRegion || '',
      country: accountHolderAddressCountry || '',
    },
    fiat: {
      accountNumber: accountNumber,
      code: bankSwiftCode || bankCode || '',
      bankName: bankName || '',
      ...(accountType && { accountType }),
      bankAddress: {
        addressLine1: bankAddressLine1 || '',
        addressLine2: bankAddressLine2 || '',
        city: bankCity || '',
        postCode: bankPostCode || '',
        region: bankRegion || '',
        country: bankCountry || '',
      },
      ...(intermediaryBank && { intermediaryBank }),
    },
  }
}

export const getAccountNumberAndBankCodeLabelKeysForBankAccount = (
  accountNumber: string,
  currency: string,
  paymentMethods: string[]
) => {
  const isSWIFT = paymentMethods?.includes(PaymentMethods.SWIFT)

  if (isSWIFT) {
    switch (currency) {
      case BeneficiaryDetailsCurrency.USD:
        return {
          accountNumberLabelKey: 'formFields.accountNumberOrIban',
          bankCodeLabelKey: 'abaRoutingNumber',
          swiftCodeLabelKey: 'bicSwiftCode',
        }
      case BeneficiaryDetailsCurrency.EUR:
        return {
          accountNumberLabelKey: 'formFields.accountNumberOrIban',
          bankCodeLabelKey: 'bic',
          swiftCodeLabelKey: 'bicSwiftCode',
        }
      case BeneficiaryDetailsCurrency.GBP:
        return {
          accountNumberLabelKey: 'formFields.accountNumberOrIban',
          bankCodeLabelKey: 'sortCode',
          swiftCodeLabelKey: 'bicSwiftCode',
        }
    }
  }

  switch (currency) {
    case BeneficiaryDetailsCurrency.EUR:
    case BeneficiaryDetailsCurrency.AED:
      return {
        accountNumberLabelKey: 'iban',
        bankCodeLabelKey: 'bic',
      }
    case BeneficiaryDetailsCurrency.GBP: {
      const isIban = isIbanFormat(accountNumber)

      return isIban
        ? {
            accountNumberLabelKey: 'iban',
            bankCodeLabelKey: 'bic',
          }
        : {
            accountNumberLabelKey: 'accountNumber',
            bankCodeLabelKey: 'sortCode',
          }
    }
    case BeneficiaryDetailsCurrency.USD:
    case BeneficiaryDetailsCurrency.ZAR:
      return {
        accountNumberLabelKey: 'accountNumber',
        bankCodeLabelKey: 'abaRoutingNumber',
      }
    default:
      return {
        accountNumberLabelKey: 'accountNumber',
        bankCodeLabelKey: 'bankCode',
      }
  }
}
